import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  listAll,
} from "firebase/storage";
import { app } from "../config/firebase";

const storage = getStorage(app);

/**
 * Storage service for uploading and managing images in Firebase Storage
 */
export const storageService = {
  /**
   * Upload an image file to Firebase Storage
   * @param itemId The ID of the bucket item
   * @param file The image file to upload
   * @param index The index of the image (for naming)
   * @returns The download URL of the uploaded image
   */
  uploadImage: async (
    itemId: string,
    file: File,
    index: number,
  ): Promise<string> => {
    try {
      const fileName = `${Date.now()}_${index}_${file.name}`;
      const fileRef = ref(storage, `items/${itemId}/images/${fileName}`);

      await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(fileRef);

      return downloadURL;
    } catch (error) {
      console.error("Error uploading image:", error);
      if (error instanceof Error) {
        if (error.message.includes("CORS")) {
          throw new Error(
            "CORS configuration error. Please check Firebase Storage CORS settings.",
          );
        }
        if (error.message.includes("auth")) {
          throw new Error(
            "Authentication error. Please ensure you are logged in.",
          );
        }
      }
      throw error;
    }
  },

  /**
   * Upload multiple images
   * @param itemId The ID of the bucket item
   * @param files The image files to upload
   * @returns Array of download URLs
   */
  uploadMultipleImages: async (
    itemId: string,
    files: File[],
  ): Promise<string[]> => {
    try {
      const uploadPromises = files.map((file, index) =>
        storageService.uploadImage(itemId, file, index),
      );
      return await Promise.all(uploadPromises);
    } catch (error) {
      console.error("Error uploading multiple images:", error);
      throw error;
    }
  },

  /**
   * Delete an image from Firebase Storage
   * @param imageUrl The download URL of the image
   */
  deleteImage: async (imageUrl: string): Promise<void> => {
    try {
      // Extract the file path from the download URL
      const decodedUrl = decodeURIComponent(imageUrl);
      const startIndex = decodedUrl.indexOf("/o/") + 3;
      const endIndex = decodedUrl.indexOf("?alt=media");
      const filePath = decodedUrl.substring(startIndex, endIndex);

      const fileRef = ref(storage, filePath);
      await deleteObject(fileRef);
    } catch (error) {
      console.error("Error deleting image:", error);
      throw error;
    }
  },

  /**
   * Delete all images for an item
   * @param itemId The ID of the bucket item
   */
  deleteItemImages: async (itemId: string): Promise<void> => {
    try {
      const folderRef = ref(storage, `items/${itemId}/images`);
      const fileList = await listAll(folderRef);

      const deletePromises = fileList.items.map((fileRef) =>
        deleteObject(fileRef),
      );

      await Promise.all(deletePromises);
    } catch (error) {
      console.error("Error deleting item images:", error);
      throw error;
    }
  },
};
